import { ApolloLink } from '@apollo/client'
import { ArgumentNode, ASTNode, DocumentNode, Kind, visit } from 'graphql'

/**
 * Operations that write shipping or read rates. The shopper picks a method from what they return,
 * so they keep rating every carrier; `checkoutSetShippingSkippingRates` controls its own skip.
 */
const rateBearingOperations: ReadonlySet<string> = new Set([
  'checkoutShippingRates',
  'checkoutSetShipping',
  'checkoutSetShippingSkippingRates',
  'expressSetShipping',
  // express and `rateFirstFallback$`, which also runs inside checkout's shipping write job
  'setShippingAddressesOnCart',
  'setShippingMethodsOnCart',
  'setGuestEmailAndShippingAddressesOnCart',
  'setHoldUntilMondayOnCart',
])

const EXCLUDE_LIVE_RATES = 'exclude_live_rates'

const excludeLiveRates: ArgumentNode = {
  kind: Kind.ARGUMENT,
  name: { kind: Kind.NAME, value: EXCLUDE_LIVE_RATES },
  value: { kind: Kind.BOOLEAN, value: true },
}

/** Variables referenced anywhere in the node, declarations aside. */
const variablesIn = (node: ASTNode): Set<string> => {
  const names = new Set<string>()
  visit(node, {
    VariableDefinition: () => false,
    Variable: (variable) => {
      names.add(variable.name.value)
    },
  })
  return names
}

/**
 * Replaces every `exclude_live_rates` on the field with one literal `true`. A declaration is
 * dropped only when the replaced arguments were its last use, since an unused one fails validation.
 */
const excludingLiveRates = (document: DocumentNode): DocumentNode => {
  const rewritten: DocumentNode = visit(document, {
    Field: (field) =>
      field.name.value === 'available_shipping_methods'
        ? {
            ...field,
            arguments: [
              ...(field.arguments ?? []).filter(
                (argument) => argument.name.value !== EXCLUDE_LIVE_RATES,
              ),
              excludeLiveRates,
            ],
          }
        : undefined,
  })
  const stillUsed = variablesIn(rewritten)
  const unused = [...variablesIn(document)].filter((name) => !stillUsed.has(name))
  return unused.length === 0
    ? rewritten
    : visit(rewritten, {
        VariableDefinition: (definition) =>
          unused.includes(definition.variable.name.value) ? null : undefined,
      })
}

/**
 * With the build's `liveRateSkipping` flag on, asks Magento to skip live carrier rates on every
 * cart response outside `rateBearingOperations`. Only the transport document changes: Apollo
 * writes the result against the caller's document, so the field keeps its argument-free cache key.
 */
export const createExcludeLiveRatesLink = (liveRateSkipping: boolean): ApolloLink => {
  if (!liveRateSkipping) {
    return new ApolloLink((operation, forward) => forward(operation))
  }
  // Apollo hands the link the same transformed document per query, and HttpLink's `print` caches
  // by document identity, so reusing the rewrite keeps both from repeating per request.
  const rewritten = new WeakMap<DocumentNode, DocumentNode>()
  return new ApolloLink((operation, forward) => {
    if (!rateBearingOperations.has(operation.operationName)) {
      const original = operation.query
      const transport = rewritten.get(original) ?? excludingLiveRates(original)
      rewritten.set(original, transport)
      operation.query = transport
    }
    return forward(operation)
  })
}
